import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlaceEntity, PlaceRequestEntity } from 'src/entities/placeEntity';
import { fetchAllPages, PagedResult } from 'src/utils/fetchAllPages';

import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from './useQueryShipmentOrderLegRuns';

const buildRequestPath: (page: number, withData: boolean) => string = (
  page,
  withData,
) => [`/api/v2/places?page=${page}`, withData ? 'with_data=1' : null].filter((maybe) => !!maybe).join('&');

const fetchPlacesPage = async (page: number): Promise<PagedResult<PlaceEntity>> => {
  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number,
    data: PlaceEntity[],
  }> = await axios.get(buildRequestPath(page, true));

  return { items: response.data.data, totalPagesCount: response.data.total_pages_count };
};

export const useQueryPlaces = (enabled: boolean) => useQuery({
  queryKey: ['places'],
  queryFn: () => fetchAllPages(fetchPlacesPage),
  enabled,
  staleTime: Infinity,
  // 再試行は fetchAllPages がページ単位で行う。ここで retry すると
  // 成功済みのページまで取り直してしまうため無効化する。
  retry: false,
});

/**
 * 地点マスタの変更は legRun の主要倉庫フラグ
 * (loading_is_main_warehouse / unloading_is_main_warehouse) の判定を変える
 * (logposeTech/log_web_issues#318)。判定は API が名称＋住所の完全一致で行うので、
 * is_main_warehouse だけでなく名称・住所の編集でも結果が変わる。leg-run 一覧は
 * staleTime: Infinity なので、ここで捨てないと配車画面が古い判定のまま残る。
 *
 * 配車画面がマウントされていなければ stale マークだけが付き、次のマウントで取り直される
 * （invalidateQueries は非アクティブなクエリを即時再取得しない）。
 *
 * Why not Promise を返さない: react-query は onSuccess が返した Promise を待つ。
 * 片方だけ返すと、もう片方の再取得を待たずに mutation が settle する。
 */
const invalidatePlacesAndLegRuns = (queryClient: QueryClient) => Promise.all([
  queryClient.invalidateQueries({ queryKey: ['places'] }),
  queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] })
]);

export const useMutationPlace = () => {
  const queryClient = useQueryClient();
  return {
    addPlace: useMutation({
      mutationFn: (place: PlaceRequestEntity) => axios.post('/api/v2/places', place),
      onSuccess: () => invalidatePlacesAndLegRuns(queryClient)
    }),
    updatePlace: useMutation({
      mutationFn: (place: PlaceEntity) => axios.put(`/api/v2/places/${place.id}`, place),
      onSuccess: () => invalidatePlacesAndLegRuns(queryClient)
    }),
    deletePlaces: useMutation({
      mutationFn: (ids: number[]) => axios.delete('/api/v2/places/bulk', { data: { ids } }),
      onSuccess: () => invalidatePlacesAndLegRuns(queryClient)
    })
  };
};
